/**
 * lib/pdiReportPdf.js
 * Canares Automation "Final Inspection Report" (Doc No PRE-8.1/8.5.1/8.4-F1)
 * as a PDF, from a PDI Report returned by GET /api/pdi-reports/dc/{dc_id}.
 *
 * Layout (A4 landscape, ruled like the paper form):
 *   header block   company | title | doc no
 *   info rows      Customer / Report No, Reference No / Order No / Date
 *   table          Sl No | Part Name | Part No | Qty | Unit | Parameter |
 *                  Specification | Method | Observation | Result
 *                  (one section per DC line; the part cells span its 5 rows)
 *   footer         Inspected By (name + signature) | Approved By (name + date)
 */
import { jsPDF } from "jspdf";

const COLS = [
  { key: "sl_no", label: "Sl No", w: 30 },
  { key: "part_name", label: "Part Name", w: 110 },
  { key: "part_no", label: "Part No", w: 80 },
  { key: "qty", label: "Qty", w: 38 },
  { key: "unit", label: "Unit", w: 36 },
  { key: "parameter", label: "Parameter", w: 105 },
  { key: "specification", label: "Specification", w: 100 },
  { key: "method", label: "Method", w: 90 },
  { key: "observation", label: "Observation", w: 140 },
  { key: "result", label: "Result", w: 41 },
];
const PART_COLS = 5; // first five columns span every parameter row of a line

export const fmtPdiDate = (iso) => {
  if (!iso) return "";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return d && m && y ? `${d}.${m}.${y}` : String(iso);
};

const fmtQty = (q) => (Number.isInteger(Number(q)) ? String(Number(q)) : Number(q).toFixed(3).replace(/0+$/, ""));

export function exportPdiReportPdf(report) {
  const doc = new jsPDF({ unit: "pt", format: "a4", orientation: "landscape" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 28;
  const W = pageW - M * 2;
  const scale = W / COLS.reduce((s, c) => s + c.w, 0);
  const cols = COLS.map((c) => ({ ...c, w: c.w * scale }));
  const colX = cols.reduce((xs, c, i) => [...xs, i === 0 ? M : xs[i - 1] + cols[i - 1].w], []);

  doc.setDrawColor(0);
  doc.setLineWidth(0.6);
  doc.setTextColor(0, 0, 0);

  const cellText = (text, x, y, w, h, { bold = false, size = 8, align = "left" } = {}) => {
    doc.setFont("helvetica", bold ? "bold" : "normal");
    doc.setFontSize(size);
    const lines = doc.splitTextToSize(String(text ?? ""), w - 6);
    const lh = size * 1.15;
    const startY = y + h / 2 - ((lines.length - 1) * lh) / 2 + size * 0.35;
    const tx = align === "center" ? x + w / 2 : x + 3;
    doc.text(lines, tx, startY, { align: align === "center" ? "center" : "left" });
  };
  const rowHeight = (texts, size = 8) => {
    doc.setFontSize(size);
    const n = Math.max(1, ...texts.map(([t, w]) => doc.splitTextToSize(String(t ?? ""), w - 6).length));
    return Math.max(16, n * size * 1.15 + 6);
  };

  const drawHeader = () => {
    let y = M;
    const h1 = 44;
    const leftW = W * 0.3;
    const rightW = W * 0.3;
    const midW = W - leftW - rightW;
    doc.rect(M, y, leftW, h1);
    doc.rect(M + leftW, y, midW, h1);
    doc.rect(M + leftW + midW, y, rightW, h1);
    cellText(report.company_name, M, y, leftW, h1, { bold: true, size: 12, align: "center" });
    cellText((report.title || "").toUpperCase(), M + leftW, y, midW, h1, { bold: true, size: 15, align: "center" });
    cellText(`Doc No: ${report.doc_no}`, M + leftW + midW, y, rightW, h1, { size: 8.5, align: "center" });
    y += h1;

    const half = W / 2;
    const info = [
      [["Customer", report.customer], ["Report No", report.report_no]],
      [["Reference No / Order No", report.reference_no], ["Date", fmtPdiDate(report.report_date)]],
    ];
    const labelW = 120;
    info.forEach((pair) => {
      const h = rowHeight(pair.map(([, v]) => [v, half - labelW]), 9);
      pair.forEach(([label, value], i) => {
        const x = M + i * half;
        doc.rect(x, y, labelW, h);
        doc.rect(x + labelW, y, half - labelW, h);
        cellText(label, x, y, labelW, h, { bold: true, size: 9 });
        cellText(value || "", x + labelW, y, half - labelW, h, { size: 9 });
      });
      y += h;
    });
    y += 8;
    return y;
  };

  const drawTableHead = (y) => {
    const h = 20;
    cols.forEach((c, i) => {
      // jsPDF text colour and fill colour share the same PDF state — reset per cell.
      doc.setFillColor(230, 230, 230);
      doc.rect(colX[i], y, c.w, h, "FD");
      cellText(c.label, colX[i], y, c.w, h, { bold: true, size: 8, align: "center" });
    });
    return y + h;
  };

  let y = drawTableHead(drawHeader());
  const FOOTER_H = 70;

  (report.lines || []).forEach((ln) => {
    const partVals = [ln.sl_no, ln.part_name, ln.part_no, fmtQty(ln.qty), ln.unit];
    const rowHs = ln.params.map((p) =>
      rowHeight([[p.parameter, cols[5].w], [p.specification, cols[6].w], [p.method, cols[7].w], [p.observation, cols[8].w]]));
    const partH = rowHeight(partVals.map((v, i) => [v, cols[i].w]));
    const extra = Math.max(0, partH - rowHs.reduce((s, h) => s + h, 0));
    rowHs[rowHs.length - 1] += extra;
    const sectionH = rowHs.reduce((s, h) => s + h, 0);

    if (y + sectionH > pageH - M - FOOTER_H) {
      doc.addPage();
      y = drawTableHead(drawHeader());
    }

    partVals.forEach((v, i) => {
      doc.rect(colX[i], y, cols[i].w, sectionH);
      cellText(v, colX[i], y, cols[i].w, sectionH, { align: i === 1 ? "left" : "center", bold: i === 2 });
    });
    let ry = y;
    ln.params.forEach((p, pi) => {
      const h = rowHs[pi];
      [p.parameter, p.specification, p.method, p.observation, p.result].forEach((v, j) => {
        const ci = PART_COLS + j;
        doc.rect(colX[ci], ry, cols[ci].w, h);
        if (ci === cols.length - 1 && v === "NG") doc.setTextColor(200, 0, 0);
        cellText(v, colX[ci], ry, cols[ci].w, h, { align: ci === cols.length - 1 ? "center" : "left", bold: ci === cols.length - 1 });
        doc.setTextColor(0, 0, 0);
      });
      ry += h;
    });
    y += sectionH;
  });

  // Footer — sign-off block.
  if (y + FOOTER_H > pageH - M) {
    doc.addPage();
    y = drawHeader();
  }
  y += 10;
  const half = W / 2;
  const rows = [
    [["Inspected By", report.inspector_name], ["Approved By", report.approved_by_name]],
    [["Inspector Signature", report.inspector_signature], ["Approval Date", report.status === "approved" ? fmtPdiDate(report.approval_date) : ""]],
  ];
  rows.forEach((pair) => {
    const h = 22;
    pair.forEach(([label, value], i) => {
      const x = M + i * half;
      doc.rect(x, y, 120, h);
      doc.rect(x + 120, y, half - 120, h);
      cellText(label, x, y, 120, h, { bold: true, size: 9 });
      cellText(value || "", x + 120, y, half - 120, h, { size: 9, bold: label === "Inspector Signature" });
    });
    y += h;
  });

  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i += 1) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.text(`${report.dc_code || ""}  ·  Page ${i} of ${pages}`, pageW - M, pageH - 12, { align: "right" });
  }

  const safe = (s) => String(s || "").replace(/[^A-Za-z0-9-]+/g, "_");
  doc.save(`PDI_${safe(report.dc_code)}_${safe(report.report_no)}.pdf`);
}
